/**
 * Deleting the account.
 *
 * The one irreversible thing in the product, so the screen is a form to read rather
 * than a button to press. It is built so that nobody reaches the final button
 * without having been shown, in their own numbers, exactly what goes:
 *
 * - **what is deleted, counted** — eleven designs and three hundred versions of
 *   them is a different decision from one design, and "all your data" hides that;
 * - **who else is affected** — colleagues who lose access, and every other
 *   organisation the person's sign-in belongs to, by name. Cloud and the SDK's
 *   licence account share one sign-in, so deleting it can take somebody out of a
 *   licence account they paid for, and they have to see that written down;
 * - **what is not ours to delete** — copies already sent, and invoices Creem keeps;
 * - **the subscription**, which is cancelled now rather than at the end of the
 *   period, with its own box to tick, because agreeing to delete data is not the
 *   same as agreeing to end a payment;
 * - **the business's name, typed**, which is what separates meaning it from
 *   clicking through.
 *
 * The service enforces every one of those conditions again. This screen explains
 * them; it is not what makes them true.
 */
import { useEffect, useState } from "react";
import { ApiError, api, helpUrl, type ClosurePreview } from "../api.js";
import { Button, Field, Note, Sheet, bytes, count, when } from "../ui/index.js";

/** The same forgiving comparison the service makes: spacing and capitals do not count. */
function normalise(name: string): string {
  return name.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("en-GB");
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}

export function DeleteAccount({ onClose }: { onClose: () => void }) {
  const [preview, setPreview] = useState<ClosurePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cancel, setCancel] = useState(false);
  const [acknowledge, setAcknowledge] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; hint?: string | undefined } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .closure()
      .then((p) => !cancelled && setPreview(p))
      .catch((e) => !cancelled && setLoadError(e instanceof Error ? e.message : "We couldn't work out what your account holds."));
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.deleteWorkspace({ confirmName: typed, cancelSubscription: cancel, acknowledgeOtherOrganisations: acknowledge });
      // A full navigation to a page that does not load Clerk. The sign-in this tab
      // holds belongs to somebody who no longer exists, and the application's own
      // shell would answer that by showing a sign-in form — the wrong thing to
      // greet somebody with who has just asked to be forgotten.
      window.location.replace(`/goodbye?cancelled=${result.subscriptionCancelled ? 1 : 0}&signin=${result.signInRemoved ? 1 : 0}`);
    } catch (e) {
      setBusy(false);
      setError(e instanceof ApiError ? { message: e.message, hint: e.hint } : { message: "Your account couldn't be deleted just now.", hint: "Nothing has changed. Try again in a minute." });
    }
  };

  const others = preview?.otherOrganisations ?? null;
  const needsCancel = Boolean(preview?.subscription.live);
  const needsAcknowledge = (others?.length ?? 0) > 0;
  const matches = preview ? normalise(typed) === normalise(preview.workspace.name) : false;
  const ready = Boolean(preview?.canDelete) && others !== null && matches && (!needsCancel || cancel) && (!needsAcknowledge || acknowledge);

  return (
    <Sheet
      title="Delete your account"
      onClose={busy ? () => {} : onClose}
      wide
      footer={
        preview?.canDelete ? (
          <>
            <Button variant="quiet" onClick={onClose} disabled={busy}>
              Keep my account
            </Button>
            <Button variant="danger" icon="trash" busy={busy} disabled={!ready} onClick={() => void submit()}>
              {busy ? "Deleting everything…" : "Delete my account permanently"}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {loadError ? (
        <Note tone="bad" title="We couldn't prepare this">
          {loadError} Nothing has been deleted.
        </Note>
      ) : !preview ? (
        <div className="skeleton" style={{ height: 260 }} />
      ) : (
        <div className="stack" style={{ gap: 18 }}>
          <p>
            This permanently deletes <strong>{preview.workspace.name}</strong> and everything in it. <strong>It can't be undone</strong>, and we can't get any
            of it back for you afterwards.{" "}
            <a href={helpUrl("/support/account/#delete-account")} target="_blank" rel="noreferrer">
              What deleting does
            </a>
          </p>

          {!preview.canDelete ? (
            <Note tone="info" title="Only an admin of this workspace can delete it">
              You can see what it holds below. Ask one of its admins, who are listed in People, if it should be deleted.
            </Note>
          ) : null}

          <section className="stack" style={{ gap: 8 }}>
            <h3>What will be deleted</h3>
            <ul className="closure-list">
              <li>{plural(preview.counts.datasets, "set of data", "sets of data")}</li>
              <li>
                {plural(preview.counts.templates, "design")}
                {preview.counts.revisions ? `, with ${plural(preview.counts.revisions, "saved version")} of them` : ""}
              </li>
              <li>{plural(preview.counts.reports, "document")}</li>
              <li>
                {plural(preview.counts.shares, "share link")}
                {preview.counts.activeShares ? ` — ${count(preview.counts.activeShares)} still working, which stop immediately` : ""}
              </li>
              <li>
                {plural(preview.counts.brands, "letterhead")} and {plural(preview.counts.folders, "folder")}
              </li>
              <li>{bytes(preview.storageBytes)} of stored files</li>
              <li>Your BroadPaper sign-in</li>
            </ul>
          </section>

          <section className="stack" style={{ gap: 8 }}>
            <h3>What isn't ours to delete</h3>
            <ul className="closure-list">
              <li>Copies of documents you have already downloaded or sent to anyone.</li>
              <li>Invoices and payment records held by Creem, our payment provider, which keeps them as merchant of record.</li>
            </ul>
          </section>

          {preview.otherMembers ? (
            <Note tone="ask" title={`${plural(preview.otherMembers, "other person", "other people")} will lose access`}>
              Everybody who belongs to this workspace loses access to it, not just you.
            </Note>
          ) : null}

          {preview.subscription.live ? (
            <div className="stack" style={{ gap: 10 }}>
              <Note tone="ask" title={`Your ${preview.subscription.plan} subscription will be cancelled`}>
                It is cancelled straight away, not at the end of the current period
                {preview.subscription.renewsAt ? ` (${when(preview.subscription.renewsAt)})` : ""}, and it will not renew. If you think you're owed a refund,
                email <a href="mailto:support@broadpaper.com">support@broadpaper.com</a> before you delete your account.
              </Note>
              {preview.canDelete ? (
                <label className="check">
                  <input type="checkbox" checked={cancel} onChange={(e) => setCancel(e.target.checked)} />
                  <span>Cancel my {preview.subscription.plan} subscription now</span>
                </label>
              ) : null}
            </div>
          ) : null}

          {others === null ? (
            <Note tone="bad" title="We couldn't check what else your sign-in is used for">
              Your sign-in may also belong to other organisations, and we won't delete it without being able to tell you which. Close this and try again in a
              minute.
            </Note>
          ) : others.length ? (
            <div className="stack" style={{ gap: 10 }}>
              <Note tone="ask" title="Your sign-in is also used somewhere else">
                Deleting your sign-in removes you from {others.length === 1 ? "this organisation" : "these organisations"} too:
                <ul className="closure-list" style={{ marginTop: 6 }}>
                  {others.map((o) => (
                    <li key={o.id}>
                      <strong>{o.name}</strong>
                    </li>
                  ))}
                </ul>
                BroadPaper Cloud and the BroadPaper developer licence account share one sign-in, so one of these may be a licence account.
              </Note>
              {preview.canDelete ? (
                <label className="check">
                  <input type="checkbox" checked={acknowledge} onChange={(e) => setAcknowledge(e.target.checked)} />
                  <span>Remove my sign-in from {others.length === 1 ? "that organisation" : "those organisations"} as well</span>
                </label>
              ) : null}
            </div>
          ) : null}

          {preview.canDelete ? (
            <Field label={`Type “${preview.workspace.name}” to confirm`} id="closure-name">
              <input id="closure-name" className="input" value={typed} autoComplete="off" spellCheck={false} onChange={(e) => setTyped(e.target.value)} disabled={busy} />
            </Field>
          ) : null}

          {error ? (
            <Note tone="bad" title={error.message}>
              {error.hint ?? ""}
            </Note>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}
